import { useContext } from 'react';

import {
  ListSubheader,
  alpha,
  Box,
  List,
  styled,
  Button,
  ListItem,
  Tooltip
} from '@mui/material';
import { NavLink as RouterLink } from 'react-router-dom';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { useAuth } from 'src/contexts/AuthContext';

import DashboardTwoToneIcon from '@mui/icons-material/DashboardTwoTone';
import SmartToyTwoToneIcon from '@mui/icons-material/SmartToyTwoTone';
import EmailTwoToneIcon from '@mui/icons-material/EmailTwoTone';
import CalendarTodayTwoToneIcon from '@mui/icons-material/CalendarTodayTwoTone';
import AssignmentTwoToneIcon from '@mui/icons-material/AssignmentTwoTone';
import GroupsTwoToneIcon from '@mui/icons-material/GroupsTwoTone';
import SettingsTwoToneIcon from '@mui/icons-material/SettingsTwoTone';
import TuneIcon from '@mui/icons-material/Tune';
import RecordVoiceOverTwoToneIcon from '@mui/icons-material/RecordVoiceOverTwoTone';
import AnalyticsTwoToneIcon from '@mui/icons-material/AnalyticsTwoTone';
import PeopleTwoToneIcon from '@mui/icons-material/PeopleTwoTone';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ImageTwoToneIcon from '@mui/icons-material/ImageTwoTone';
import BrushTwoToneIcon from '@mui/icons-material/BrushTwoTone';
import BeachAccessTwoToneIcon from '@mui/icons-material/BeachAccessTwoTone';
import TextSnippetTwoToneIcon from '@mui/icons-material/TextSnippetTwoTone';
import DescriptionTwoToneIcon from '@mui/icons-material/DescriptionTwoTone';
import CloudTwoToneIcon from '@mui/icons-material/CloudTwoTone';
import PaymentTwoToneIcon from '@mui/icons-material/PaymentTwoTone';
import RequestQuoteTwoToneIcon from '@mui/icons-material/RequestQuoteTwoTone';

interface SidebarMenuProps {
  collapsed?: boolean;
}

interface MenuWrapperProps {
  collapsed?: boolean;
}

const MenuWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})<MenuWrapperProps>(
  ({ theme, collapsed }) => `
  .MuiList-root {
    padding: ${theme.spacing(collapsed ? 0.5 : 1)};

    & > .MuiList-root {
      padding: 0 ${theme.spacing(0)} ${theme.spacing(1)};
    }
  }

    .MuiListSubheader-root {
      text-transform: uppercase;
      font-weight: bold;
      font-size: ${theme.typography.pxToRem(10)};
      color: ${theme.colors.alpha.trueWhite[50]};
      padding: ${theme.spacing(0, collapsed ? 1 : 2)};
      line-height: 1.4;
      ${collapsed ? 'display: none;' : ''}
    }
`
);

interface SubMenuWrapperProps {
  collapsed?: boolean;
}

const SubMenuWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})<SubMenuWrapperProps>(
  ({ theme, collapsed }) => `
    .MuiList-root {

      .MuiListItem-root {
        padding: 1px 0;

        .MuiBadge-root {
          position: absolute;
          right: ${theme.spacing(3.2)};

          .MuiBadge-standard {
            background: ${theme.colors.primary.main};
            font-size: ${theme.typography.pxToRem(10)};
            font-weight: bold;
            text-transform: uppercase;
            color: ${theme.palette.primary.contrastText};
          }
        }

        .MuiButton-root {
          display: flex;
          color: ${theme.colors.alpha.trueWhite[70]};
          background-color: transparent;
          width: 100%;
          justify-content: ${collapsed ? 'center' : 'flex-start'};
          padding: ${theme.spacing(collapsed ? 1 : 0.8, collapsed ? 1 : 2)};
          min-height: ${collapsed ? '40px' : '36px'};
          border-radius: 6px;

          .MuiButton-startIcon,
          .MuiButton-endIcon {
            transition: ${theme.transitions.create(['color'])};

            .MuiSvgIcon-root {
              font-size: inherit;
              transition: none;
            }
          }

          .MuiButton-startIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            font-size: ${theme.typography.pxToRem(collapsed ? 22 : 18)};
            margin-right: ${collapsed ? '0' : theme.spacing(1)};
            margin-left: ${collapsed ? '0' : '0'};
          }

          .MuiButton-endIcon {
            color: ${theme.colors.alpha.trueWhite[50]};
            margin-left: auto;
            opacity: .8;
            font-size: ${theme.typography.pxToRem(18)};
          }

          &.active,
          &:hover {
            background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.08)};
            color: ${theme.colors.alpha.trueWhite[100]};

            .MuiButton-startIcon,
            .MuiButton-endIcon {
              color: ${theme.colors.alpha.trueWhite[100]};
            }
          }

          &.active {
            background-color: ${alpha(theme.colors.primary.main, 0.2)};

            .MuiButton-startIcon {
              color: ${theme.colors.primary.main};
            }
          }
        }

        &.Mui-children {
          flex-direction: column;

          .MuiBadge-root {
            position: absolute;
            right: ${theme.spacing(7)};
          }
        }

        .MuiCollapse-root {
          width: 100%;

          .MuiList-root {
            padding: ${theme.spacing(1, 0)};
          }

          .MuiListItem-root {
            padding: 1px 0;

            .MuiButton-root {
              padding: ${theme.spacing(0.8, 3)};

              .MuiBadge-root {
                right: ${theme.spacing(3.2)};
              }

              &:before {
                content: ' ';
                background: ${theme.colors.alpha.trueWhite[100]};
                opacity: 0;
                transition: ${theme.transitions.create([
                  'transform',
                  'opacity'
                ])};
                width: 6px;
                height: 6px;
                transform: scale(0);
                transform-origin: center;
                border-radius: 20px;
                margin-right: ${theme.spacing(1.8)};
              }

              &.active,
              &:hover {

                &:before {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            }
          }
        }
      }
    }
`
);

// Highlighted AI Agent Button
interface AIAgentButtonWrapperProps {
  collapsed?: boolean;
}

const AIAgentButtonWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})<AIAgentButtonWrapperProps>(
  ({ theme, collapsed }) => `
    padding: ${theme.spacing(collapsed ? 0.5 : 1)};
    padding-bottom: 0;

    .ai-agent-button {
      display: flex;
      align-items: center;
      justify-content: ${collapsed ? 'center' : 'flex-start'};
      width: 100%;
      padding: ${theme.spacing(collapsed ? 1 : 0.8, collapsed ? 1 : 2)};
      border-radius: 6px;
      background: transparent;
      color: #FFD700;
      font-weight: 600;
      font-size: ${theme.typography.pxToRem(13)};
      text-transform: none;
      transition: all 0.2s ease;

      .MuiButton-startIcon {
        color: #FFD700;
        margin-right: ${collapsed ? '0' : theme.spacing(1)};
        margin-left: 0;

        .MuiSvgIcon-root {
          font-size: ${theme.typography.pxToRem(collapsed ? 22 : 20)};
        }
      }

      &:hover {
        background-color: ${alpha(theme.colors.alpha.trueWhite[100], 0.08)};
        color: #FFE44D;

        .MuiButton-startIcon {
          color: #FFE44D;
        }
      }

      &.active {
        background-color: ${alpha('#FFD700', 0.15)};
        color: #FFD700;

        .MuiButton-startIcon {
          color: #FFD700;
        }
      }
    }
`
);

function SidebarMenu({ collapsed = false }: SidebarMenuProps) {
  const { closeSidebar } = useContext(SidebarContext);
  const { user } = useAuth();

  const menuItems = [
    {
      section: 'Dashboard',
      items: [
        { to: '/dashboards/main', icon: <DashboardTwoToneIcon />, label: 'Overview' },
        { to: '/dashboards/emails', icon: <EmailTwoToneIcon />, label: 'Emails' },
        { to: '/dashboards/calendar', icon: <CalendarTodayTwoToneIcon />, label: 'Calendar' },
        { to: '/dashboards/tasks', icon: <AssignmentTwoToneIcon />, label: 'Tasks' },
        { to: '/dashboards/team', icon: <GroupsTwoToneIcon />, label: 'Team' },
        { to: '/dashboards/attendance', icon: <BeachAccessTwoToneIcon />, label: 'Zoho' },
        { to: '/dashboards/elevenlabs', icon: <RecordVoiceOverTwoToneIcon />, label: 'Voice Studio' },
        { to: '/dashboards/audome', icon: <BrushTwoToneIcon />, label: 'Audome Studio' },
        { to: '/dashboards/captions', icon: <TextSnippetTwoToneIcon />, label: 'Captions' },
        { to: '/dashboards/scripts', icon: <DescriptionTwoToneIcon />, label: 'Scripts' },
        { to: '/dashboards/drive', icon: <CloudTwoToneIcon />, label: 'Drive' },
        { to: '/dashboards/settings', icon: <TuneIcon />, label: 'Settings' }
      ]
    },
    {
      section: 'Management',
      items: [
        { to: '/management/user-management', icon: <PeopleTwoToneIcon />, label: 'User Management' }
      ]
    }
  ];

  const adminItems = [
    { to: '/dashboards/agent-analytics', icon: <AnalyticsTwoToneIcon />, label: 'AI Analytics' },
    { to: '/admin/payment', icon: <PaymentTwoToneIcon />, label: 'Payment' },
    { to: '/admin/profit-tracker', icon: <RequestQuoteTwoToneIcon />, label: 'Profit Tracker' },
    // Triggers page hidden from menu but accessible via direct URL: /admin/triggers
    { to: '/admin/settings', icon: <SettingsTwoToneIcon />, label: 'Super Settings' }
  ];

  const renderMenuItem = (item: { to: string; icon: JSX.Element; label: string }) => {
    const button = (
      <Button
        disableRipple
        component={RouterLink}
        onClick={closeSidebar}
        to={item.to}
        startIcon={item.icon}
      >
        {!collapsed && item.label}
      </Button>
    );

    if (collapsed) {
      return (
        <ListItem component="div" key={item.to}>
          <Tooltip title={item.label} placement="right" arrow>
            {button}
          </Tooltip>
        </ListItem>
      );
    }

    return (
      <ListItem component="div" key={item.to}>
        {button}
      </ListItem>
    );
  };

  const aiAgentButton = (
    <Button
      disableRipple
      component={RouterLink}
      onClick={closeSidebar}
      to="/dashboards/agent"
      startIcon={collapsed ? <SmartToyTwoToneIcon /> : <AutoAwesomeIcon />}
      className="ai-agent-button"
    >
      {!collapsed && 'AI Agent'}
    </Button>
  );

  return (
    <>
      {/* AI Agent Button - Always on top */}
      <AIAgentButtonWrapper collapsed={collapsed}>
        {collapsed ? (
          <Tooltip title="AI Agent" placement="right" arrow>
            {aiAgentButton}
          </Tooltip>
        ) : (
          aiAgentButton
        )}
      </AIAgentButtonWrapper>

      <MenuWrapper collapsed={collapsed}>
        {menuItems.map((section) => (
          <List
            key={section.section}
            component="div"
            subheader={
              !collapsed ? (
                <ListSubheader component="div" disableSticky>
                  {section.section}
                </ListSubheader>
              ) : null
            }
          >
            <SubMenuWrapper collapsed={collapsed}>
              <List component="div">
                {section.items.map(renderMenuItem)}
              </List>
            </SubMenuWrapper>
          </List>
        ))}

        {(user?.is_super_admin || user?.role === 'Admin') && (
          <List
            component="div"
            subheader={
              !collapsed ? (
                <ListSubheader component="div" disableSticky>
                  Admin
                </ListSubheader>
              ) : null
            }
          >
            <SubMenuWrapper collapsed={collapsed}>
              <List component="div">
                {adminItems.map(renderMenuItem)}
              </List>
            </SubMenuWrapper>
          </List>
        )}
      </MenuWrapper>
    </>
  );
}

export default SidebarMenu;
